import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { safeArray } from "@/lib/utils";
import type { Group, GroupInvitation } from "@/types";
import { useGroupStore } from "@/stores/groupStore";
import { queryKeys } from "@/lib/queryKeys";

export function useMyInvitations() {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: queryKeys.myInvitations(),
    queryFn: async () => {
      const { data } = await api.get<GroupInvitation[]>("/invitations");
      return data;
    },
  });

  const acceptMutation = useMutation({
    mutationFn: async (invitation: GroupInvitation): Promise<Group> => {
      const { data: group } = await api.post<Group>(`/invitations/${invitation.uuid}/accept`);
      return group;
    },
    onSuccess: (group) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.myInvitations() });
      // Add accepted group to the store
      const { groups: currentGroups } = useGroupStore.getState();
      useGroupStore.setState({
        groups: [...currentGroups, group],
        activeGroup: group,
      });
    },
  });

  const accept = (invitation: GroupInvitation) => acceptMutation.mutateAsync(invitation);

  return {
    invitations: safeArray(query.data),
    isLoading: query.isLoading,
    error: query.error?.message ?? null,
    refetch: query.refetch,
    accept,
  };
}
